import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getCaseStudy } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { SITE_URL, DEFAULT_OG_IMAGE, breadcrumbLd } from "@/lib/seo";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";
import { amp } from "@/lib/amp";

function NarrativeBlock({ label, text }) {
  if (!text) return null;
  return (
    <div data-testid={`case-section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--copper-deep)] font-normal">{label}</p>
      <p className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.6] text-[color:var(--lead-ink)] mt-2.5">
        {text}
      </p>
    </div>
  );
}

export default function CaseStudyPage() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const { openBrief } = useBrief();

  const { data: cs, status } = useQuery({
    queryKey: ["case-study", slug],
    queryFn: () => getCaseStudy(slug),
    retry: false,
  });

  useEffect(() => {
    if (cs) track("case_study_page_view", { slug });
  }, [cs, slug]);

  const fade = {
    initial: { opacity: 0, y: reduce ? 0 : 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduce ? 0 : 0.5, ease: "easeOut" },
  };

  const service = cs?.service;
  const path = `/work/${slug}`;

  return (
    <div className="exec-scope min-h-screen">
      <article className="exec-container pt-24 md:pt-28 pb-24">
        <div className="max-w-3xl">
          <Link to="/work" data-testid="case-back" className="exec-link">
            <ArrowLeft className="h-4 w-4" /> All work
          </Link>

          {status === "pending" && (
            <div className="mt-10 animate-pulse" data-testid="case-loading">
              <div className="h-3 w-28 bg-[color:var(--rule)]" />
              <div className="h-12 w-3/4 bg-[color:var(--rule)] mt-6" />
              <div className="h-4 w-full bg-[color:var(--rule)] mt-8" />
              <div className="space-y-3 mt-10">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-4 w-full bg-[color:var(--rule)]" />
                ))}
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="mt-12" data-testid="case-notfound">
              <p className="exec-eyebrow">Case study</p>
              <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">This case isn&rsquo;t available.</h1>
              <p className="exec-serif text-[18px] text-[color:var(--lead-ink)] mt-5 max-w-xl">
                It may have been moved or unpublished. The rest of the work is still here.
              </p>
              <Link to="/work" className="exec-btn exec-btn-primary mt-7" data-testid="case-notfound-back">
                Back to Work <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {status === "success" && cs && (
            <motion.div {...fade} data-testid="case-article">
              <Seo
                title={`${cs.headline} — Copper & Vine`}
                description={cs.lead || cs.pull_line || cs.headline}
                path={path}
                type="article"
                image={DEFAULT_OG_IMAGE}
                jsonLd={[
                  {
                    "@context": "https://schema.org",
                    "@type": "CreativeWork",
                    name: cs.headline,
                    headline: cs.headline,
                    abstract: cs.lead || undefined,
                    about: cs.type || undefined,
                    keywords: [cs.type, cs.sector_label, cs.the_work].filter(Boolean).join(", ") || undefined,
                    author: { "@type": "Organization", name: "Copper & Vine" },
                    provider: { "@type": "Organization", name: "Copper & Vine", url: SITE_URL },
                    publisher: { "@type": "Organization", name: "Copper & Vine", logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE } },
                    image: DEFAULT_OG_IMAGE,
                    mainEntityOfPage: `${SITE_URL}${path}`,
                    url: `${SITE_URL}${path}`,
                  },
                  breadcrumbLd([
                    { name: "Home", path: "/" },
                    { name: "Work", path: "/work" },
                    { name: cs.headline, path },
                  ]),
                ]}
              />

              <header className="mt-9">
                <p className="exec-eyebrow" data-testid="case-page-eyebrow">{amp(cs.type)}</p>
                <h1
                  className="font-extralight text-[clamp(30px,4.6vw,54px)] leading-[1.08] mt-5"
                  data-testid="case-page-headline"
                >
                  {cs.headline}
                </h1>
                {cs.lead && (
                  <p
                    className="exec-serif text-[clamp(19px,2.2vw,24px)] leading-[1.5] text-[color:var(--lead-ink)] mt-6"
                    data-testid="case-page-lead"
                  >
                    {cs.lead}
                  </p>
                )}
              </header>

              <div className="h-px w-16 bg-[color:var(--copper)] my-10" />

              <div className="space-y-9" data-testid="case-narrative">
                <NarrativeBlock label="The problem" text={cs.problem_detail} />
                <NarrativeBlock label="The caliber" text={cs.caliber_detail} />
                <NarrativeBlock label="The work" text={cs.work_detail} />
                <NarrativeBlock label="The outcome" text={cs.outcome_detail} />
              </div>

              {cs.through_line && (
                <blockquote
                  data-testid="case-through-line"
                  className="mt-11 border-l-2 border-[color:var(--copper)] pl-6"
                >
                  <p className="exec-serif italic text-[clamp(20px,2.6vw,28px)] leading-[1.4] text-[color:var(--ink)]">
                    {cs.through_line}
                  </p>
                </blockquote>
              )}

              {(cs.detail_line || cs.timeframe) && (
                <p
                  data-testid="case-page-detail-line"
                  className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--grey)] font-normal mt-10"
                >
                  {cs.detail_line || `${cs.type} · ${cs.the_work} · ${cs.timeframe}`}
                </p>
              )}

              {/* Close: relevant Service + Scorecard + brief */}
              <div className="mt-14 pt-10 border-t border-[color:var(--rule)]">
                <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.15]">
                  Is this the problem you&rsquo;re carrying?
                </h2>
                <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4 max-w-xl">
                  Score where you stand in ten questions, or see the engagement built for exactly this.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-4">
                  <Link to="/scorecard" data-testid="case-scorecard-link" className="exec-btn exec-btn-primary">
                    Take the Scorecard <ArrowRight className="h-4 w-4" />
                  </Link>
                  {service?.path && (
                    <Link
                      to={service.path}
                      data-testid="case-service-link"
                      onClick={() => track("case_service_click", { slug, service: service.path })}
                      className="exec-btn exec-btn-secondary"
                    >
                      {amp(service.label || "See the service")}
                    </Link>
                  )}
                  <button
                    type="button"
                    data-testid="case-brief-link"
                    onClick={() => {
                      track("brief_open", { location: "case_study", slug });
                      openBrief("general");
                    }}
                    className="exec-link"
                  >
                    Request the brief
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </article>
    </div>
  );
}
